import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { BaseComponent } from '../../../core';
import { TimeInterval } from '../../../services';

@Component({ template: '' })
export abstract class OpeningHoursPickerBaseComponent extends BaseComponent implements OnChanges {
  @Output() openingHoursChange: EventEmitter<TimeInterval[]>;
  @Output() remove: EventEmitter<unknown>;

  private _heading: string;
  private _description: string;
  private _showRemove = false;
  private _allowInherit = false;
  private _isOpen: boolean;
  private _isOpenAllDay: boolean;
  private _inheritNormalHours: boolean;
  private _openingHours: TimeInterval[];
  private _defaultOpeningHours: TimeInterval[];
  private previousOpeningHours: TimeInterval[];

  constructor(private cd: ChangeDetectorRef) {
    super();

    this.openingHoursChange = new EventEmitter<TimeInterval[]>();
    this.remove = new EventEmitter<unknown>();
    this._defaultOpeningHours = [new TimeInterval()];
  }

  get heading(): string {
    return this._heading;
  }
  @Input() set heading(value: string) {
    this._heading = value;
    this.cd.markForCheck();
  }

  get description(): string {
    return this._description;
  }
  @Input() set description(value: string) {
    if (!value || value === this._description) {
      return;
    }
    this._description = value;
    this.cd.markForCheck();
  }

  get showRemove(): boolean {
    return this._showRemove;
  }
  @Input() set showRemove(value: boolean) {
    this._showRemove = value;
    this.cd.markForCheck();
  }

  get allowInherit(): boolean {
    return this._allowInherit;
  }
  @Input() set allowInherit(value: boolean) {
    this._allowInherit = value;
    this.cd.markForCheck();
  }

  get isOpen(): boolean {
    return this._isOpen;
  }
  set isOpen(value: boolean) {
    this._isOpen = value;
    this.cd.markForCheck();
  }

  get isOpenAllDay(): boolean {
    return this._isOpenAllDay;
  }
  set isOpenAllDay(value: boolean) {
    if (value === this._isOpenAllDay) {
      // No change
      return;
    }
    if (value) {
      this.savePreviousOpeningHours();
      this.openingHours = [{ from: 0, to: 1440 }];
    } else {
      this.restorePreviousOpeningHours();
    }
    this._isOpenAllDay = value;
    this.cd.markForCheck();
  }

  get inheritNormalHours(): boolean {
    return this._inheritNormalHours;
  }
  set inheritNormalHours(value: boolean) {
    if (value === this._inheritNormalHours) {
      // No change
      return;
    }
    if (value) {
      this.savePreviousOpeningHours();
      this.openingHours = null;
    } else {
      this.restorePreviousOpeningHours();
    }
    this._inheritNormalHours = value;
    this.cd.markForCheck();
  }

  get isClosed(): boolean {
    return !this.isOpen;
  }
  set isClosed(value: boolean) {
    this.isOpen = !value;

    if (!this.isOpen) {
      this._defaultOpeningHours = this.openingHours;
      this.openingHours = [];
    } else {
      this.openingHours = this._defaultOpeningHours;
    }
    this.openingHoursChange.emit(this.openingHours);
  }

  get openingHours(): TimeInterval[] {
    return this._openingHours;
  }
  @Input() set openingHours(value: TimeInterval[]) {
    this._openingHours = value;
    this.cd.markForCheck();

    this.openingHoursChange.emit(value);
  }

  get isCompleted(): boolean {
    if (this.isClosed || this.openingHours === null) {
      return true;
    }
    for (let i = 0; i < this.openingHours.length; i++) {
      if (!TimeInterval.isValid(this.openingHours[i])) {
        return false;
      }
    }
    return true;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.openingHours) {
      return;
    }
    const isOpen = this.isInherit(this.openingHours) || (this.openingHours && this.openingHours[0] !== undefined);
    if (isOpen !== this.isOpen) {
      this.isOpen = isOpen;
    }
    const isOpenAllDay = this.is24Hour(this.openingHours);
    if (isOpenAllDay !== this.isOpenAllDay) {
      this._isOpenAllDay = isOpenAllDay;
    }
    const inheritNormalHours = this.isInherit(this.openingHours);
    if (inheritNormalHours !== this._inheritNormalHours) {
      this._inheritNormalHours = inheritNormalHours;
    }
  }

  savePreviousOpeningHours() {
    if (!this.openingHours || this.is24Hour(this.openingHours)) {
      return;
    }
    this.previousOpeningHours = this.copyOpeningHours(this.openingHours);
  }

  restorePreviousOpeningHours() {
    if (!this.previousOpeningHours || this.is24Hour(this.previousOpeningHours)) {
      return;
    }
    this.openingHours = this.previousOpeningHours;
    this.previousOpeningHours = null;
  }

  is24Hour(openingHours: TimeInterval[]): boolean {
    if (!openingHours || openingHours.length !== 1) {
      return false;
    }
    return openingHours[0].from === 0 && openingHours[0].to === 1440;
  }

  isInherit(openingHours: TimeInterval[]): boolean {
    return openingHours === null;
  }

  addTimeInterval() {
    this.openingHours.push(new TimeInterval());
  }

  removeTimeInterval(index: number) {
    this.openingHours.splice(index, 1);
  }

  toggleIsOpen() {
    this.isOpen = !this.isOpen;

    if (!this.isOpen) {
      this._defaultOpeningHours = this.openingHours;
      this.openingHours = [];
    } else {
      this.openingHours = this._defaultOpeningHours;
    }
    this.openingHoursChange.emit(this.openingHours);
  }

  requestRemove() {
    this.remove.emit();
  }

  printDebugInfo() {
    console.log(`this.heading: ${this.heading}`);
    console.log(`this.openingHours`, this.openingHours);
  }

  onTimeIntervalChange(_e) {
    this.openingHoursChange.emit(this.openingHours);
  }

  private copyOpeningHours(value: TimeInterval[]): TimeInterval[] {
    if (!value) {
      return value;
    }
    return value.map((t) => new TimeInterval(t.from, t.to));
  }
}
